import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoaderCircle, Plus, UserRound, AlertCircle } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import ContentItem from "../../../../components/layout/content/ContentItem";

import ContentGrid from "../../../../components/grids/content/ContentGrid";
import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";
import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";

import { useCreators } from "../hooks/useCreators";

import "./CreatorsView.css";

export default function CreatorsView({ userId }) {
  const navigate = useNavigate();

  const { creators, loading, error, getCreators } = useCreators(userId);

  useEffect(() => {
    if (!userId) return;

    void getCreators();
  }, [userId]);

  const creatorList = Array.isArray(creators) ? creators : [];

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error.message || "Unable to load creator profiles."
    : "";

  const handleCreateCreator = () => {
    navigate("/creators/create");
  };

  const renderCreator = (creator) => {
    const creatorName = creator.brandName || creator.username || "Creator";

    const username = String(creator.username || "").trim();

    return (
      <ContentItem
        key={creator.creatorId ?? creator.username}
        className="creator-profile-card"
      >
        <button
          type="button"
          className="creator-profile-card__button"
          disabled={!username}
          onClick={() => navigate(`/creators/${encodeURIComponent(username)}`)}
        >
          <div className="creator-profile-card__avatar">
            {creator.avatarUrl ? (
              <img src={creator.avatarUrl} alt="" loading="lazy" />
            ) : (
              <UserRound size={28} aria-hidden="true" />
            )}
          </div>

          <div className="creator-profile-card__content">
            <Heading as="h3" size="sm">
              {creatorName}
            </Heading>

            {username && (
              <Text as="span" size="sm" tone="secondary">
                @{username}
              </Text>
            )}
          </div>
        </button>
      </ContentItem>
    );
  };

  return (
    <ViewContainer>
      <ViewHeader
        title="Creator Studio"
        description="Manage your creator profiles."
        actions={
          <Button
            value="Create"
            variant="primary"
            icon={<Plus size={16} />}
            onClick={handleCreateCreator}
          />
        }
      />

      {loading && creatorList.length === 0 ? (
        <div className="creators-loading" role="status">
          <LoaderCircle
            className="creators-spinner"
            size={28}
            aria-hidden="true"
          />

          <Text as="p" tone="secondary" size="sm">
            Loading creator profiles...
          </Text>
        </div>
      ) : errorMessage && creatorList.length === 0 ? (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title="Unable to load creators"
          description={errorMessage}
          action={
            <Button
              value="Try Again"
              variant="primary-outlined"
              onClick={getCreators}
            />
          }
        />
      ) : creatorList.length === 0 ? (
        <EmptyState
          icon={<UserRound size={28} />}
          title="No creator profiles"
          description="Create a creator profile to start publishing content."
          action={
            <Button
              value="Create Creator"
              variant="primary"
              icon={<Plus size={16} />}
              onClick={handleCreateCreator}
            />
          }
        />
      ) : (
        <>
          {errorMessage && (
            <div className="creators-error" role="alert">
              <Text as="p" size="sm">
                {errorMessage}
              </Text>
            </div>
          )}

          <ContentGrid
            items={creatorList}
            columns={3}
            tabletColumns={2}
            mobileColumns={1}
            gap={16}
            showEmpty={false}
            renderItem={renderCreator}
          />
        </>
      )}
    </ViewContainer>
  );
}
